import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router";
import { useAuth } from "react-oidc-context";

import { getTenant } from "@/api/tenants";
import { StatusDot, type Tone } from "@/components/ui";
import { useShellState } from "@/hooks/useShellState";
import type { TenantMetadata, TenantStage } from "@/types/api";

// The tree draws a stage's state as a dot only; the label is for a reader who cannot see it.
function stageState(stage: TenantStage): { tone: Tone; label: string } {
  if (stage.isDestroyed) return { tone: "neutral", label: "destroyed" };
  if (stage.environmentClaimed && stage.loginReady) return { tone: "success", label: "ready" };
  return { tone: "warning", label: "pending" };
}

// One class list per state, never two that both set a colour: a row darkens a step under the
// pointer, and the current one (the tenant or the environment being looked at) wears the gold tint.
const ROW = "flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs transition hover:bg-sunken-deep";
const ROW_CURRENT = "bg-brand-100 text-brand-700";
const ROW_OTHER = "text-ink";

export function TreeNode({ tenant }: { tenant: TenantMetadata }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId: activeTenantId } = useParams<{ tenantId: string }>();
  const { isTenantExpanded, toggleTenant } = useShellState();

  const expanded = isTenantExpanded(tenant.tenantId);
  const isActiveTenant = activeTenantId === tenant.tenantId;

  // Lazy: only fetch stages once the node is expanded. Shares the cache key
  // with TenantDetail so expanding a node pre-warms its page.
  const detail = useQuery({
    queryKey: ["tenant", tenant.tenantId],
    queryFn: () => getTenant(token, tenant.tenantId),
    enabled: !!token && expanded,
  });
  const stages = detail.data?.stages ?? [];

  function onTenantKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowRight" && !expanded) {
      e.preventDefault();
      toggleTenant(tenant.tenantId);
    } else if (e.key === "ArrowLeft" && expanded) {
      e.preventDefault();
      toggleTenant(tenant.tenantId);
    }
  }

  return (
    <li role="treeitem" aria-expanded={expanded}>
      <div className="flex items-center">
        <button
          type="button"
          data-tree-item="true"
          aria-label={expanded ? `Collapse ${tenant.tenantId}` : `Expand ${tenant.tenantId}`}
          onClick={() => toggleTenant(tenant.tenantId)}
          onKeyDown={onTenantKeyDown}
          className="flex h-6 w-5 shrink-0 items-center justify-center rounded text-ink-muted transition hover:text-ink"
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${expanded ? "rotate-90" : ""}`}
            aria-hidden="true"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
        <button
          type="button"
          data-tree-item="true"
          onClick={() => navigate(`/tenants/${tenant.tenantId}`)}
          onKeyDown={onTenantKeyDown}
          title={tenant.displayName}
          className={`${ROW} ${isActiveTenant && !location.hash ? ROW_CURRENT : ROW_OTHER}`}
        >
          <span className="min-w-0 flex-1 truncate font-medium">
            {tenant.displayName}
          </span>
        </button>
      </div>

      {expanded && (
        <ul role="group" className="ml-4 border-l border-line pl-1.5">
          {detail.isLoading && (
            <li className="px-2 py-1 text-xs text-ink-muted">Loading…</li>
          )}
          {detail.isError && (
            <li className="px-2 py-1 text-xs text-danger-fg">
              Loading error
            </li>
          )}
          {!detail.isLoading && !detail.isError && stages.length === 0 && (
            <li className="px-2 py-1 text-xs text-ink-muted">
              No environment
            </li>
          )}
          {stages.map((stage) => {
            const hash = `#stage-${stage.stage}`;
            const isActiveStage = isActiveTenant && location.hash === hash;
            const state = stageState(stage);
            return (
              <li key={stage.stage} role="treeitem">
                <button
                  type="button"
                  data-tree-item="true"
                  onClick={() =>
                    navigate(`/tenants/${tenant.tenantId}${hash}`)
                  }
                  title={stage.stage}
                  className={`${ROW} ${isActiveStage ? ROW_CURRENT : ROW_OTHER}`}
                >
                  <StatusDot tone={state.tone} size="md" />
                  <span className="min-w-0 flex-1 truncate">
                    {stage.stage}
                    <span className="sr-only"> — {state.label}</span>
                  </span>
                  {stage.isProduction && (
                    <span className="shrink-0 text-xs uppercase tracking-wider text-warning-fg">
                      prod
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
